import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import {
    Checkbox,
    Label,
    LabeledControl,
    Radio,
    Spacer,
    Text,
} from '@zillow/constellation';
import { Col } from '@zillow/zrs-comet';

import { amenityTypes, laundryTypes, petTypes } from 'app/shared/constants/AmenitiesAndRulesConstants';
import AdditionalAmenities from 'app/shared/components/postingPath/AdditionalAmenities';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import NotificationConstants from 'app/shared/constants/NotificationConstants';
import PostingActions from 'app/shared/actions/PostingActions';

import { StyledAnchor } from '../UtilsStyledComponents';
import { StyledRow } from './StyledComponents';

const { SUCCESS } = NotificationConstants;
const { LISTING_COMPONENT_GROUP_NAME } = AnalyticsConstants;
const SAVING = 'Saving...';

const propTypes = {
    formData: PropTypes.object.isRequired,
    errorObj: PropTypes.object.isRequired,
    updateErrorObj: PropTypes.func.isRequired,
    immediateUpdateAndSave: PropTypes.func.isRequired,
    isInputFormDisabled: PropTypes.bool.isRequired,
    updateFormData: PropTypes.func.isRequired,
    updateStatusMessage: PropTypes.func.isRequired,
};

const AmenitiesAndRules = ({
    formData,
    errorObj,
    updateErrorObj,
    immediateUpdateAndSave,
    isInputFormDisabled = false,
    updateFormData,
    updateStatusMessage,
}) => {
    const dispatch = useDispatch();

    const {
        amenities,
        listingAlias,
        petsAllowed: {
            cats,
            smallDogs,
            largeDogs,
        },
    } = formData;

    const setLaundryType = (type) => {
        updateStatusMessage(SAVING);

        return dispatch(PostingActions.property.edit({
            fieldName: 'laundry',
            value: type,
            propertyId: listingAlias,
        }))
            .then(() => {
                updateFormData({ laundry: type });
                updateStatusMessage('All data saved!', SUCCESS);
            });
    };

    const updatePets = (newPetPolicyStatus) => {
        updateStatusMessage(SAVING);
        const updatedPetData = {
            cats,
            smallDogs,
            largeDogs,
        };

        switch (newPetPolicyStatus) {
            case 'none':
                updatedPetData.cats = false;
                updatedPetData.smallDogs = false;
                updatedPetData.largeDogs = false;
                break;
            case 'smallDogs':
                updatedPetData.smallDogs = !smallDogs;
                break;
            case 'largeDogs':
                updatedPetData.largeDogs = !largeDogs;
                break;
            case 'cats':
                updatedPetData.cats = !cats;
                break;
            default:
                return;
        }
        updateFormData({
            petsAllowed: {
                ...updatedPetData,
                none: !updatedPetData.cats && !updatedPetData.smallDogs && !updatedPetData.largeDogs,
            },
        });

        dispatch(PostingActions.property.pets({ ...updatedPetData, propertyId: listingAlias }))
            .then(() => updateStatusMessage('All data saved!', SUCCESS));
    };

    return (
        <div className="AmenitiesAndRules">
            <Text
                marginBottom="xs"
                mediaQuery={{
                    lg: {
                        marginBottom: 'sm',
                    },
                }}
                fontType="h4"
                as="div"
            >
                Amenities and rules
            </Text>
            <StyledAnchor />
            <Label>Amenities</Label>
            {amenityTypes.map(amenity => (
                <LabeledControl
                    control={(
                        <Checkbox
                            checked={formData[amenity.formKey] === true}
                            onChange={() => immediateUpdateAndSave(
                                amenity.formKey,
                                !formData[amenity.formKey],
                                {
                                    componentGroupName: LISTING_COMPONENT_GROUP_NAME.AMENITIES,
                                }
                            )}
                        />
                    )}
                    controlId={amenity.controlId}
                    data-test-id="amenity-control"
                    disabled={isInputFormDisabled}
                    key={amenity.label}
                    label={<Label>{amenity.label}</Label>}
                    size="md"
                />
            ))}
            <Spacer marginY="xs" />
            <StyledRow>
                <Col xs={12} lg={7}>
                    <AdditionalAmenities
                        label="Additional Amenities"
                        listingAlias={listingAlias}
                        previouslySavedAmenities={amenities}
                        updateFormData={updateFormData}
                        errorObj={errorObj}
                        updateErrorObj={updateErrorObj}
                        updateStatusMessage={updateStatusMessage}
                    />
                </Col>
            </StyledRow>
            <Spacer marginY="xs" />
            <Label>Laundry</Label>
            {laundryTypes.map(laundry => (
                <LabeledControl
                    control={(
                        <Radio
                            name="LabeledControl-Laundry"
                            checked={formData.laundry === laundry.apiKey}
                            onChange={() => setLaundryType(laundry.apiKey)}
                        />
                    )}
                    controlId={laundry.controlId}
                    data-test-id="laundry-control"
                    disabled={isInputFormDisabled}
                    label={<Label>{laundry.label}</Label>}
                    key={laundry.label}
                />
            ))}
            <Spacer marginY="xs" />
            <Label>Pets</Label>
            {petTypes.map(petType => (
                <LabeledControl
                    control={(
                        <Checkbox
                            checked={formData.petsAllowed[petType.formKey]}
                            onChange={() => updatePets(petType.formKey)}
                        />
                    )}
                    controlId={petType.controlId}
                    data-test-id="pets-control"
                    disabled={isInputFormDisabled}
                    label={<Label>{[petType.label]}</Label>}
                    key={petType.label}
                    size="md"
                />
            ))}
            <Spacer marginY="md" />
        </div>
    );
};

AmenitiesAndRules.propsTypes = propTypes;

export default AmenitiesAndRules;
